import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { CellClassParams, CellStyle, ColDef } from 'ag-grid-community';
import type { CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useTranslation } from 'react-i18next';
import TableGridItem from '../UI/TableGridItem';
import { BillingConfigWithRelations } from '../../services/budgetBillingConfig';
import { PendingConfigEdits } from '../../hooks/usePendingConfigEdits';
import { useCurrentAppMonth } from '../../hooks/useCurrentAppMonth';
import { useBudgetContext, useBudgetWindow } from '../../store/Budget/BudgetContext';
import {
    generateMonthColumns,
    getVisibleMonths,
    getWindowMonths,
    currencyFormatter,
    parseBudgetCell,
    MONTH_KEY,
} from './helpers';
import { buildIdentifierRow, getIdentifierColumns } from './identifierColumns';
import { pendingBorder } from './pendingCellStyle';

interface BonusGridProps {
    billingConfigs: BillingConfigWithRelations[];
    isLoading: boolean;
    /** The page's pending edits — this grid records into them; the page saves them (CALL-779). */
    pending: PendingConfigEdits;
    /** CALL-781: hide month COLUMNS before the current month. Display only. */
    hidePastMonths?: boolean;
}

// CALL-745: bonuses are global master data; editable for budget users.
// CALL-779: an edit is recorded in the page's pending store rather than saved on the spot.
const BonusGrid = ({ billingConfigs, isLoading, pending, hidePastMonths = false }: BonusGridProps) => {
    const gridRef = useRef<AgGridReact>(null);
    const { t } = useTranslation('budget');
    const { canEdit } = useBudgetContext();
    const { windowStart } = useBudgetWindow();

    // CALL-781: columns only — the row build below reads the config's own values, not this list.
    const currentMonth = useCurrentAppMonth();
    const months = useMemo(() => getWindowMonths(windowStart), [windowStart]);
    const visibleMonths = useMemo(
        () => getVisibleMonths(months, hidePastMonths, currentMonth),
        [months, hidePastMonths, currentMonth]
    );

    // Served values per config, and this grid's slice of the pending set — see useRateGrid for why both
    // are indexed up front rather than looked up per cell.
    const servedByConfig = useMemo(
        () =>
            new Map(
                billingConfigs.map((config) => [
                    config.id,
                    new Map((config.bonuses || []).map((bonus) => [String(bonus.month), Number(bonus.amount)])),
                ])
            ),
        [billingConfigs]
    );

    const pendingByConfig = useMemo(() => {
        const byConfig = new Map<number, Map<string, number | null>>();

        for (const edit of pending.edits) {
            if (edit.kind !== 'bonusEdits') continue;
            const forConfig = byConfig.get(edit.rowId) ?? new Map<string, number | null>();
            forConfig.set(edit.key, edit.value as number | null);
            byConfig.set(edit.rowId, forConfig);
        }

        return byConfig;
    }, [pending.edits]);

    const monthColumns = useMemo(
        () =>
            generateMonthColumns(visibleMonths, {
                minWidth: 100,
                editable: canEdit,
                useValueFormatterForExport: false,
                valueFormatter: (params) => {
                    if (params.value == null || params.value === '') return '';
                    return currencyFormatter.format(Number(params.value));
                },
                // The shared budget cell parser (German-pinned, CALL-794): a bonus pasted from Excel as
                // "1.250,00 €" is 1250, and an emptied cell answers null — the batch save's "delete this
                // month".
                valueParser: (params) => parseBudgetCell(params.newValue),
                cellStyle: (params: CellClassParams): CellStyle => {
                    const field = params.colDef?.field;

                    return {
                        ...(canEdit ? { backgroundColor: '#fffde7' } : {}),
                        ...pendingBorder(
                            Boolean(
                                field &&
                                params.data?.id != null &&
                                pendingByConfig.get(Number(params.data.id))?.has(field)
                            )
                        ),
                    };
                },
            }),
        [visibleMonths, canEdit, pendingByConfig]
    );

    const rowData = useMemo(
        () =>
            billingConfigs.map((config) => {
                const row: Record<string, unknown> = {
                    id: config.id,
                    ...buildIdentifierRow(config),
                };

                for (const [month, amount] of servedByConfig.get(config.id) ?? []) {
                    row[month] = amount;
                }

                // CALL-779: pending edits are layered over the served values, so an unsaved bonus survives
                // the background refetch that follows a save elsewhere on the page.
                for (const [month, value] of pendingByConfig.get(config.id) ?? []) {
                    if (value === null) delete row[month];
                    else row[month] = Number(value);
                }

                return row;
            }),
        [billingConfigs, servedByConfig, pendingByConfig]
    );

    // Style-only changes (the red border appearing or going) do not re-run cellStyle on their own — the
    // same ag-grid behaviour CALL-752 documented in useRateGrid.
    useEffect(() => {
        gridRef.current?.api?.refreshCells({ force: true });
    }, [rowData, pendingByConfig]);

    const handleCellValueChanged = useCallback(
        (event: CellValueChangedEvent) => {
            const { data, colDef } = event;
            if (!data?.id || !colDef.field || !MONTH_KEY.test(colDef.field)) return;

            const configId = Number(data.id);
            const monthKey = colDef.field;
            // Already through the column's valueParser, so a number or null — never a raw paste string.
            const value = data[monthKey] == null ? null : Number(data[monthKey]);
            pending.set(configId, 'bonusEdits', monthKey, value, servedByConfig.get(configId)?.get(monthKey));
        },
        [pending, servedByConfig]
    );

    const identifierColumns = useMemo(() => getIdentifierColumns(t), [t]);

    const columnDefs = useMemo(() => [...identifierColumns, ...monthColumns], [identifierColumns, monthColumns]);

    const defaultColDef: ColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    // CALL-794: `budget-grid` — the class App.css's inline-editor height rule is scoped to. See
    // MinutePriceGrid for what its absence looked like (a 72px editor over the next row).
    return (
        <Box className="budget-grid">
            <TableGridItem
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                onCellValueChanged={handleCellValueChanged}
                getRowId={(params) => String(params.data.id)}
                loading={isLoading}
                cellSelection
                height={'auto'}
                noRowsOverlayComponent={() => <span style={{ padding: 10 }}>{t('hint.noBonuses')}</span>}
            />
        </Box>
    );
};

export default BonusGrid;
